import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  HostBinding,
  Injector,
  Input,
  OnDestroy,
  Signal,
  StaticProvider,
  ViewChild,
  ViewContainerRef,
  booleanAttribute,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { classnames } from '@fmnts/common';
import { ChangeCallback, TouchedCallback } from '@fmnts/common/forms';
import { isDefined } from '@fmnts/core';
import {
  DECLARATIVE_FORMS_FIELD_BUILDER_TOKEN,
  DeclarativeForm,
  FieldDefinition,
  FieldNodeInputDefinitionList,
  FieldsetDefinition,
  FormDeclaration,
  GraphBuilder,
  GraphNodeInputList,
  IDeclarativeFieldInputComponent,
  IDeclarativeFieldOutputComponent,
  isFormInputField,
} from '@fmnts/declarative-forms/core';
import * as P from 'effect/Predicate';
import { MatomoTracker } from 'ngx-matomo-client';
import { Observable, Subject, debounceTime, merge } from 'rxjs';

type FieldChangeType = 'step-visible' | 'step-not-visible';

interface FieldChange {
  /** Source emitting the change. */
  source: unknown;
  /** Describes the type of change to the field.  */
  type: FieldChangeType;
}

/**
 * Describes the interface for an instantiated component
 * for the current field.
 */
type DeclarativeFieldComponentInstance = IDeclarativeFieldOutputComponent &
  Partial<IDeclarativeFieldInputComponent<unknown>>;
type DeclarativeInputFieldComponentInstance = IDeclarativeFieldOutputComponent &
  IDeclarativeFieldInputComponent<unknown>;

@Component({
  selector: 'declarative-field',
  template: '<ng-container #entry></ng-container>',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DeclarativeFieldComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DeclarativeFieldComponent
  implements AfterContentInit, OnDestroy, ControlValueAccessor
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly graphBuilder = inject(GraphBuilder);
  private readonly fieldBuilder = inject(DECLARATIVE_FORMS_FIELD_BUILDER_TOKEN);
  private readonly destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);
  readonly #tracker = inject(MatomoTracker);

  @Input({ required: true }) declaration!: FormDeclaration;
  @Input({ required: true }) field!: FieldDefinition;
  @Input({ required: true }) fieldset!: FieldsetDefinition;
  @Input({ required: true }) form!: DeclarativeForm;

  /**
   * ´true´, if the field is on the currently visible step.
   */
  @Input() get isCurrentStep(): boolean {
    return this._isCurrentStep();
  }
  set isCurrentStep(value: BooleanInput) {
    const previousValue = this._isCurrentStep();
    this._isCurrentStep.set(coerceBooleanProperty(value));
    const isCurrentStep = this._isCurrentStep();

    if (previousValue !== isCurrentStep) {
      this.stateChange$.next({
        source: this,
        type: isCurrentStep ? 'step-visible' : 'step-not-visible',
      });
    }
  }
  private readonly _isCurrentStep = signal(false);

  /**
   * Checks for first element if enabled in {@link NextDeclarativeFormsModule}
   */
  readonly autofocus = input(false, { transform: booleanAttribute });

  @ViewChild('entry', { read: ViewContainerRef, static: true })
  entry!: ViewContainerRef;
  @HostBinding('class.generic-form-field') componentClass =
    'generic-form-field';

  @HostBinding('class.form-field--hidden')
  public get isHidden(): boolean {
    return this.control.disabled;
  }

  @HostBinding('class') get key(): string {
    return classnames([
      `field-key--${this.field.key}`,
      this.fieldBuilder.getFieldStyles(this.field, this.fieldPath),
    ]);
  }

  /** Emits when the fields state has changed. */
  readonly stateChange$ = new Subject<FieldChange>();

  private component?: DeclarativeFieldComponentInstance;
  private _value: unknown;
  private _onChange: ChangeCallback = () => {};
  private _onTouched: TouchedCallback = () => {};

  /** Path to the field */
  private get fieldPath(): [string, string] {
    return [this.fieldset.key, this.field.key];
  }

  // TODO: Inject control, using `NgControl`
  public get control(): AbstractControl<any, any> {
    const control = this.form.get(this.fieldPath);
    if (!control) {
      throw new Error(
        `Failed to fetch Control for path: ${this.fieldPath.join('.')}`,
      );
    }
    return control;
  }

  ngOnDestroy(): void {
    this.stateChange$.complete();
  }

  ngAfterContentInit(): void {
    // Create component
    const componentType =
      this.fieldBuilder.resolveFieldComponentType<DeclarativeFieldComponentInstance>(
        this.field,
      );
    // TODO(angular-20): Prefer using inputBinding / outputBinding
    const componentRef = this.entry.createComponent(componentType);
    this.component = componentRef.instance;

    // Set up inputs
    this.#setupCommonInputs(componentRef);
    this.#setupInputFieldInputs(componentRef);

    this.component.registerOnChange?.((v: unknown) => this._onChange(v));
    this.component.registerOnTouched?.(() => this._onTouched());
    this.component.writeValue?.(this._value);

    this._initializeFieldGraph();
    this._intializeAnalyticsTracking();
  }

  private get selfProvider() {
    return [{ provide: DeclarativeFieldComponent, useValue: this }];
  }

  writeValue(value: unknown): void {
    if (this.component && this.component.writeValue) {
      this.component.writeValue(value);
    } else {
      this._value = value;
    }
  }

  registerOnChange(fn: ChangeCallback): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: TouchedCallback): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.component?.setDisabledState?.(isDisabled);
  }

  /**
   * Set up common inputs shared by all field components.
   */
  #setupCommonInputs(
    componentRef: ComponentRef<DeclarativeFieldComponentInstance>,
  ) {
    // TODO(angular-20): Prefer using inputBinding
    // see https://angular.dev/api/core/inputBinding
    componentRef.setInput('declaration', this.declaration);
    componentRef.setInput('field', this.field);
    componentRef.setInput('fieldset', this.fieldset);
    componentRef.setInput('form', this.form);

    this.#bindInput(componentRef, 'isCurrentStep', this._isCurrentStep);
  }

  /**
   * Set up inputs shared by all input field components.
   */
  #setupInputFieldInputs(
    componentRef: ComponentRef<DeclarativeFieldComponentInstance>,
  ): void {
    const component = componentRef.instance;

    // TODO: use setInput once `control` is an signal.
    component.control = this.control;

    if (!isInputFieldComponentRef(componentRef, this.field)) {
      return;
    }

    if (P.hasProperty(component, 'autofocus')) {
      this.#bindInput(componentRef, 'autofocus', this.autofocus);
    }
  }

  resetFocus(): void {
    this.component?.resetAutofocus?.();
  }

  // TODO(angular-20): Prefer using inputBinding
  // see https://angular.dev/api/core/inputBinding
  /**
   * Binds a signal to an input property of a component.
   * @param componentRef The component reference.
   * @param inputName The name of the input property.
   * @param signalFn Signal that should be bound to the input.
   */
  #bindInput<C, Input extends keyof C & string>(
    componentRef: ComponentRef<C>,
    inputName: Input,
    signalFn: C[Input] extends infer T | Signal<infer T> ? Signal<T> : never,
  ): void {
    componentRef.setInput(inputName, signalFn());
    effect(
      () => {
        componentRef.setInput(inputName, signalFn());
      },
      { injector: this.#injector },
    );
  }

  private _intializeAnalyticsTracking() {
    const trackingOpts = this.fieldBuilder.trackFieldValueChanges?.(this.field);

    if (trackingOpts) {
      this.control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this.#tracker.trackEvent(
            trackingOpts.category,
            trackingOpts.action,
            trackingOpts.namePrefix + value,
          );
        });
    }
  }

  private _initializeFieldGraph() {
    const providers = [this.selfProvider];
    const fieldInputs = this._fieldGraphNodeInputs(providers);
    const metaInputs = this._sharedGraphNodeInputs(providers);

    // If there is an observable for the value, subscribe
    const valueSources = [fieldInputs.value, metaInputs.value].filter(
      isDefined,
    );
    if (valueSources.length > 0) {
      merge(...valueSources)
        .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this._updateValueFromGraph(value);
        });
    }

    // If there is an observable for input readonly state, subscribe
    const readonlySources = [
      fieldInputs.isReadonly,
      metaInputs.isReadonly,
    ].filter(isDefined) as Observable<boolean>[];
    if (readonlySources.length > 0) {
      merge(...readonlySources)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((readonly) => {
          this._updateIsReadonlyFromGraph(readonly);
        });
    }
  }

  private _updateValueFromGraph(value: unknown) {
    const { control } = this;
    if (!control) {
      return;
    }

    // Update value
    this.writeValue(value);
    control.patchValue(value, { emitModelToViewChange: false });
    this.cd.markForCheck();
  }

  private _updateIsReadonlyFromGraph(readonly: boolean) {
    if (this.component && P.hasProperty(this.component, 'isReadonly')) {
      this.component.isReadonly = readonly;
      this.cd.detectChanges();
    }
  }

  /**
   * Creates graph node inputs that belong only to the current field.
   *
   * @returns
   */
  private _fieldGraphNodeInputs(
    providers: StaticProvider[],
  ): GraphNodeInputList<FieldNodeInputDefinitionList> {
    // Those definitions could come from the backend in the future
    // meaning, that with the campaign setup you could customize dynamic behaviour.
    // But for now, we use fake definitions.
    const definitions =
      this.graphBuilder.prepareDefinition<FieldNodeInputDefinitionList>(
        providers,
      );

    return this._buildGraphNodeInputs(definitions, providers);
  }

  /**
   * Creates the shared graph node inputs for the current field.
   *
   * @returns
   */
  private _sharedGraphNodeInputs(
    providers: StaticProvider[],
  ): GraphNodeInputList<FieldNodeInputDefinitionList> {
    const definitions = this.fieldBuilder.getSharedFieldGraphDefinitions?.(
      this.field,
    );

    return this._buildGraphNodeInputs(definitions, providers);
  }

  private _buildGraphNodeInputs(
    definition: FieldNodeInputDefinitionList | undefined,
    providers: StaticProvider[],
  ): GraphNodeInputList<FieldNodeInputDefinitionList> {
    return !definition || Object.keys(definition).length === 0
      ? {}
      : this.graphBuilder.buildInputs(definition, providers);
  }
}

function isInputFieldComponentRef(
  ref: ComponentRef<DeclarativeFieldComponentInstance>,
  field: FieldDefinition,
): ref is ComponentRef<DeclarativeInputFieldComponentInstance> {
  // This is sort of a hack as the function itself does not use the given component reference to
  // determine the type of the field.
  return isFormInputField(field);
}
